import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { RotateCcw, Search, Trash2 } from "lucide-react";

import Spinner from "../../components/spinner";
import Pagination from "../../components/pagination";
import { useLanguage } from "../../context/languageContext";
import { usePermissions } from "../../context/permissionContext";
import { useConfirm } from "../../components/confirmDialog";
import { formatWhen } from "../comments/commentsApi";
import { TRASH_TYPES, TYPE_INFO, groupByType, trashApi } from "./trashApi";
import styles from "./Trash.module.scss";

/**
 * Trash — books, summaries, posts, authors, journeys, recommenders and series
 * deleted in the last 30 days. Restore puts one back exactly as it was: same
 * id (so readers' saves, shelves and progress reconnect), its summaries if it
 * was a book, and its place in every series, journey and recommender list.
 * After 30 days the server purges it on its own.
 *
 * Restore and "Delete forever" need `delete` on the item's own feature — the
 * same right that deleted it. The tabs only show what the admin can view.
 *
 * The tab, search and page live in the URL, like the other list pages.
 */

const SEARCH_DELAY = 300;
const PAGE_SIZE = 25;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Red in the last 3 days, amber in the last week. */
const DaysLeft = ({ days }) => {
  const tone = days <= 3 ? "bg-danger" : days <= 7 ? "bg-warning text-dark" : "bg-secondary";
  return (
    <span className={`badge ${tone}`}>
      {days <= 0 ? "Purging today" : `${plural(days, "day")} left`}
    </span>
  );
};

const Trash = () => {
  const { adminInfo } = useLanguage();
  const { can } = usePermissions();
  const confirm = useConfirm();
  const api = useMemo(() => trashApi(adminInfo?.token), [adminInfo?.token]);

  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const query = params.get("q") || "";
  const page = Math.max(parseInt(params.get("page"), 10) || 1, 1);

  const [searchInput, setSearchInput] = useState(query);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  // Rows being worked on, by trash id, so their buttons disable.
  const [busy, setBusy] = useState(() => new Set());
  // Ticked rows, keyed by trash id and holding the row — its type decides
  // which endpoint it goes to. A Map so a selection can span pages and tabs.
  const [selected, setSelected] = useState(() => new Map());

  const canDelete = useCallback((row) => can(TYPE_INFO[row.type]?.feature, "delete"), [can]);

  const setUrl = useCallback(
    (changes) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== query) setUrl({ q: searchInput, page: "" });
    }, SEARCH_DELAY);
    return () => clearTimeout(id);
  }, [searchInput, query, setUrl]);

  const load = useCallback(async () => {
    if (!adminInfo?.token) return;
    setLoading(true);
    try {
      const data = await api.list({
        page,
        limit: PAGE_SIZE,
        type: type || undefined,
        search: query || undefined,
      });
      setRows(data?.items || []);
      setCounts(data?.counts || {});
      setTotal(data?.total || 0);
      setPages(data?.pages || 1);
    } catch (error) {
      toast.error(error.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminInfo?.token, api, page, query, type]);

  useEffect(() => {
    load();
  }, [load]);

  // Only the types the server returned a count for — i.e. the ones this admin
  // can view. "All" sums them.
  const tabs = TRASH_TYPES.filter((t) => t.type in counts);
  const allCount = tabs.reduce((sum, t) => sum + (counts[t.type] || 0), 0);

  const markBusy = (ids, on) =>
    setBusy((current) => {
      const next = new Set(current);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const unselect = (ids) =>
    setSelected((current) => {
      const next = new Map(current);
      ids.forEach((id) => next.delete(id));
      return next;
    });

  /**
   * Restores rows, one request per type. Partial success is reported as what
   * it is, with the server's reason for each refusal — "restore the book
   * first" is an instruction, not an error to swallow.
   */
  const restore = async (picked) => {
    const ids = picked.map((r) => r._id);
    markBusy(ids, true);
    const restored = [];
    const failed = [];
    try {
      for (const [rowType, trashIds] of Object.entries(groupByType(picked))) {
        try {
          const result = await api.restore(rowType, trashIds);
          restored.push(...(result.restored || []).map((r) => ({ ...r, type: rowType })));
          failed.push(...(result.failed || []));
        } catch (error) {
          trashIds.forEach((id) => failed.push({ trashId: id, reason: error.message }));
        }
      }

      if (restored.length) {
        // Without `publish` the server brings live content back as a draft.
        const rowOf = (r) => picked.find((p) => String(p._id) === String(r.trashId));
        const asDraft = restored.some((r) => rowOf(r)?.status === 1 && !can(TYPE_INFO[r.type]?.feature, "publish"));
        toast.success(
          (restored.length === 1 && restored[0].label ? `“${restored[0].label}” restored` : `${plural(restored.length, "item")} restored`) +
            (asDraft ? " as a draft — someone with publish rights can make it live." : ".")
        );
      }
      if (failed.length) {
        const label = (f) => f.label || picked.find((r) => String(r._id) === String(f.trashId))?.label || "An item";
        toast.warn(
          <div>
            <div className="fw-semibold mb-1">Could not restore {plural(failed.length, "item")}</div>
            {failed.slice(0, 3).map((f) => (
              <div key={String(f.trashId)} className="small">
                {label(f)} — {f.reason}
              </div>
            ))}
            {failed.length > 3 && <div className="small">…and {failed.length - 3} more</div>}
          </div>,
          { autoClose: 6000 }
        );
      }
      unselect(restored.map((r) => r.trashId));
      await load();
    } finally {
      markBusy(ids, false);
    }
  };

  const purge = async (picked) => {
    const ok = await confirm({
      title: picked.length === 1 ? "Delete this forever?" : `Delete ${picked.length} items forever?`,
      body: `${picked.length === 1 ? "It leaves" : "They leave"} the trash now instead of after 30 days. This can't be undone.`,
      quote: picked
        .slice(0, 5)
        .map((r) => r.label || `Untitled ${TYPE_INFO[r.type]?.one || "item"}`)
        .join("\n")
        .concat(picked.length > 5 ? `\n…and ${picked.length - 5} more` : ""),
      confirmLabel: "Delete forever",
    });
    if (!ok) return;

    const ids = picked.map((r) => r._id);
    markBusy(ids, true);
    try {
      // One request per type, each standing alone like restore's: a refusal on
      // one type must not hide that another's items are already gone for good.
      const gone = [];
      const errors = [];
      for (const [rowType, trashIds] of Object.entries(groupByType(picked))) {
        try {
          const result = await api.purge(rowType, trashIds);
          gone.push(...(result?.purged || []).map((r) => String(r._id)));
        } catch (error) {
          errors.push(error.message);
        }
      }
      if (gone.length) toast.success(`${plural(gone.length, "item")} permanently deleted`);
      if (errors.length) toast.error(errors.join(" "));
      unselect(gone);
      await load();
    } finally {
      markBusy(ids, false);
    }
  };

  // Only rows the admin can act on are tickable.
  const actionable = rows.filter(canDelete);
  const pageSelected = actionable.length > 0 && actionable.every((r) => selected.has(r._id));
  const someSelected = actionable.some((r) => selected.has(r._id));

  const toggleRow = (row) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(row._id)) next.delete(row._id);
      else next.set(row._id, row);
      return next;
    });

  const togglePage = () =>
    setSelected((current) => {
      const next = new Map(current);
      actionable.forEach((row) => {
        if (pageSelected) next.delete(row._id);
        else next.set(row._id, row);
      });
      return next;
    });

  const picked = [...selected.values()];
  const bulkBusy = picked.some((r) => busy.has(r._id));

  return (
    <div className="p-3">
      <div className="mb-1">
        <h4 className="mb-1">Trash {allCount > 0 ? `(${allCount})` : ""}</h4>
        <p className="text-muted small mb-0">
          Deleted content stays here for 30 days and can be restored exactly as it was — then it is
          removed for good.
        </p>
      </div>

      <div className={styles.tabs} role="tablist">
        {[{ type: "", label: "All" }, ...tabs].map((t) => (
          <button
            key={t.type || "all"}
            type="button"
            role="tab"
            aria-selected={type === t.type}
            className={type === t.type ? styles.tabActive : styles.tab}
            onClick={() => setUrl({ type: t.type, page: "" })}
          >
            {t.label}
            <span className={styles.tabCount}>{t.type ? counts[t.type] || 0 : allCount}</span>
          </button>
        ))}
      </div>

      <div className="position-relative mb-3">
        <Search
          size={16}
          className="position-absolute text-muted"
          style={{ left: 12, top: "50%", transform: "translateY(-50%)" }}
        />
        <input
          className="form-control form-control-sm ps-5"
          style={{ height: 40 }}
          placeholder="Search by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {selected.size > 0 && (
        <div className="d-flex align-items-center gap-3 mb-3 p-2 px-3 box_wrapper">
          <span className="small">{plural(selected.size, "item")} selected</span>
          <button
            type="button"
            className="btn btn-sm btn-link text-muted p-0"
            onClick={() => setSelected(new Map())}
          >
            Clear
          </button>
          <div className="ms-auto d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-dark d-inline-flex align-items-center gap-2"
              disabled={bulkBusy}
              onClick={() => restore(picked)}
            >
              <RotateCcw size={14} />
              Restore selected
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-2"
              disabled={bulkBusy}
              onClick={() => purge(picked)}
            >
              <Trash2 size={14} />
              Delete forever
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <Trash2 size={28} className="mb-2" />
          <p className="mb-0">{query ? "Nothing in the trash matches that." : "The trash is empty."}</p>
        </div>
      ) : (
        <>
          <div className="table_scrollbar table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  {actionable.length > 0 && (
                    <th>
                      <input
                        className="box form-check-input"
                        type="checkbox"
                        checked={pageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !pageSelected;
                        }}
                        onChange={togglePage}
                        aria-label="Select every item on this page"
                      />
                    </th>
                  )}
                  <th className="max_width">Name</th>
                  <th>Type</th>
                  <th>Deleted by</th>
                  <th>Deleted</th>
                  <th>Purged in</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const info = TYPE_INFO[row.type];
                  const allowed = canDelete(row);
                  const rowBusy = busy.has(row._id);
                  return (
                    <tr key={row._id}>
                      {actionable.length > 0 && (
                        <td className="checkBox">
                          {allowed && (
                            <input
                              className="box form-check-input"
                              type="checkbox"
                              checked={selected.has(row._id)}
                              onChange={() => toggleRow(row)}
                              aria-label={`Select ${row.label || info?.one}`}
                            />
                          )}
                        </td>
                      )}
                      <td className="max_width">
                        {row.label || <span className="text-muted">Untitled {info?.one}</span>}
                        {row.status === 0 && <span className={styles.draft}>Draft</span>}
                        {row.childCount > 0 && (
                          <div className="text-muted small">
                            With {plural(row.childCount, "summary", "summaries")} — restored together
                          </div>
                        )}
                      </td>
                      <td>{info?.label || row.type}</td>
                      <td>{row.deletedBy?.adminName || <span className="text-muted">—</span>}</td>
                      <td>{formatWhen(row.deletedAt)}</td>
                      <td>
                        <DaysLeft days={row.daysLeft} />
                      </td>
                      <td>
                        {allowed ? (
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                              disabled={rowBusy}
                              onClick={() => restore([row])}
                            >
                              <RotateCcw size={14} />
                              Restore
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={rowBusy}
                              onClick={() => purge([row])}
                              aria-label="Delete forever"
                              title="Delete forever"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted small">View only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(next) => setUrl({ page: String(next) })}
              showItemsPerPage={false}
              showTotalItems
            />
          )}
        </>
      )}
    </div>
  );
};

export default Trash;
